import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Wallet } from 'lucide-react';
import { settingsApi, productsApi, ordersApi, customersApi } from '../api';
import { formatPrice } from '../store';
import { SkeletonCards } from '../components/ui/Skeleton';
import type { Settings, ProductListItem, OrderListItem, Customer } from '../types';

interface StatCardProps {
  title: string;
  value: string | number;
  /** Valyuta belgisi alohida — raqam bilan bitta uzun so'zga yopishmasin. */
  unit?: string;
  icon: React.ReactNode;
  color: string;
  link?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, unit, icon, color, link }) => {
  const content = (
    // Ixcham chip: ikonka matn yonida, kartaning ichki bo'shlig'i kichik.
    // Ilgari bu blok ekranning yarmidan ko'pini egallardi — ro'yxatlar
    // pastga tushib ketardi.
    <div className={`card !p-3 h-full flex items-center gap-2.5 min-w-0 ${link ? 'hover:shadow-md transition-shadow cursor-pointer' : ''}`}>
      <div className={`p-1.5 rounded-lg shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        {/* Sarlavha kesilmaydi — sig'masa ikkinchi qatorga o'tadi. */}
        <p className="text-[11px] sm:text-xs text-gray-500 leading-tight">{title}</p>
        {/* `break-words` emas: bo'lingan summa ikkita alohida raqamdek o'qiladi.
            Sig'masa uch nuqta chiqsin — bu hech bo'lmasa davomi borligini bildiradi. */}
        <p className="text-sm sm:text-lg font-bold text-gray-900 leading-tight tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">
          {value}
          {unit && (
            <span className="ml-1 text-[10px] sm:text-xs font-semibold text-gray-500">{unit}</span>
          )}
        </p>
      </div>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block h-full">
        {content}
      </Link>
    );
  }
  return content;
};

/** Uchala ro'yxatdagi qator bitta shaklda: chapda nom + izoh, o'ngda son. */
const Row: React.FC<{
  title: string;
  subtitle?: string;
  value: string;
  valueClass?: string;
  note?: string;
  noteClass?: string;
}> = ({ title, subtitle, value, valueClass = 'text-gray-900', note, noteClass }) => (
  <div className="flex items-center justify-between gap-3 py-2.5 min-w-0">
    <div className="min-w-0 flex-1">
      <p className="font-medium text-sm truncate">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
    </div>
    <div className="text-right shrink-0">
      <p className={`font-bold text-sm tabular-nums whitespace-nowrap ${valueClass}`}>{value}</p>
      {note && <p className={`text-xs whitespace-nowrap ${noteClass || 'text-gray-400'}`}>{note}</p>}
    </div>
  </div>
);

type TabKey = 'orders' | 'debts' | 'lowStock';

export const Dashboard: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [lowStockProducts, setLowStockProducts] = useState<ProductListItem[]>([]);
  const [recentOrders, setRecentOrders] = useState<OrderListItem[]>([]);
  const [debtCustomers, setDebtCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>('orders');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, lowStockRes, ordersRes, debtRes] = await Promise.all([
          settingsApi.get(),
          productsApi.lowStock(),
          ordersApi.list({ page_size: 5 }),
          customersApi.withDebt(),
        ]);

        const asArray = <T,>(v: unknown): T[] => {
          if (Array.isArray(v)) return v as T[];
          if (v && typeof v === 'object' && Array.isArray((v as { results?: unknown }).results)) {
            return (v as { results: T[] }).results;
          }
          return [];
        };

        setSettings(settingsRes.data);
        setLowStockProducts(asArray<ProductListItem>(lowStockRes.data));
        setRecentOrders(asArray<OrderListItem>(ordersRes.data));
        setDebtCustomers(asArray<Customer>(debtRes.data));
      } catch (error) {
        console.error('Dashboard data fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalDebt = debtCustomers.reduce((sum, c) => sum + Number(c.debt), 0);

  const tabs = [
    { key: 'orders' as TabKey, label: 'Buyurtmalar', count: recentOrders.length, link: '/orders', empty: "Buyurtmalar yo'q" },
    { key: 'debts' as TabKey, label: 'Qarzlar', count: debtCustomers.length, link: '/customers?filter=with_debt', empty: "Qarzli mijoz yo'q" },
    { key: 'lowStock' as TabKey, label: 'Kam qolgan', count: lowStockProducts.length, link: '/products?filter=low_stock', empty: "Kam qolgan tovar yo'q" },
  ];
  const activeTab = tabs.find((t) => t.key === tab) || tabs[0];

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="page-bar text-2xl font-bold text-gray-900">Dashboard</h1>
        <SkeletonCards cards={2} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="page-bar text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 items-stretch">
        <StatCard
          title="Dollar kursi"
          value={formatPrice(settings?.usd_rate || 0)}
          unit="UZS"
          icon={<DollarSign className="text-success-600" size={16} />}
          color="bg-success-50"
          link="/settings"
        />
        {/* Kam qolgan tovarlar va qarzli mijozlar SONI bu yerda emas —
            ular pastdagi tab bejetlarida turibdi. Qarz SUMMASI esa hech
            qayerda takrorlanmaydi, shuning uchun qoladi. */}
        <StatCard
          title="Umumiy qarz"
          value={formatPrice(totalDebt)}
          unit="UZS"
          icon={<Wallet className="text-danger-600" size={16} />}
          color="bg-danger-50"
          link="/customers?filter=with_debt"
        />
      </div>

      {/* Ro'yxatlar bitta kartada: uchta alohida karta ekranning qolgan
          qismini ham to'ldirib yuborardi, buyurtmalarni ko'rish uchun uzoq
          aylantirish kerak edi. Endi tab bilan almashadi. */}
      <div className="card">
        {/* "Barchasini ko'rish" havolasi tab qatorida emas, karta ostida:
            telefonda uchta tab bilan bitta qatorga sig'masdi. */}
        <div className="flex w-full sm:w-fit gap-1 bg-primary-50 rounded-full p-1 mb-3">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex-1 sm:flex-none min-w-0 flex items-center justify-center gap-1 rounded-full px-1.5 sm:px-4 py-1.5 text-[11px] sm:text-xs font-medium transition-colors ${
                tab === t.key ? 'bg-primary-600 text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="truncate">{t.label}</span>
              <span
                className={`shrink-0 rounded-full px-1.5 text-[10px] font-semibold ${
                  tab === t.key ? 'bg-white/25 text-white' : 'bg-primary-100 text-primary-800'
                }`}
              >
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {activeTab.count === 0 ? (
          <p className="text-gray-400 text-center py-6 text-sm">{activeTab.empty}</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {tab === 'orders' && recentOrders.slice(0, 5).map((order) => (
              <Row
                key={order.id}
                title={`#${order.number}`}
                subtitle={order.customer_name || "Noma'lum mijoz"}
                value={`${formatPrice(order.total)} UZS`}
                note={order.status_display}
                noteClass={
                  order.status === 'paid' ? 'text-success-600' :
                  order.status === 'partial' ? 'text-warning-600' :
                  'text-gray-400'
                }
              />
            ))}

            {tab === 'debts' && debtCustomers.slice(0, 5).map((customer) => (
              <Row
                key={customer.id}
                title={customer.name}
                subtitle={customer.phone}
                value={`${formatPrice(customer.debt)} UZS`}
                valueClass="text-danger-600"
              />
            ))}

            {tab === 'lowStock' && lowStockProducts.slice(0, 5).map((product) => (
              <Row
                key={product.id}
                title={product.name}
                subtitle={product.sku}
                value={`${product.stock} ta`}
                valueClass={product.stock === 0 ? 'text-danger-600' : 'text-warning-600'}
              />
            ))}
          </div>
        )}

        <Link
          to={activeTab.link}
          className="block text-center text-primary-800 text-xs font-medium hover:underline border-t border-gray-100 pt-3 mt-1"
        >
          Barchasini ko'rish
        </Link>
      </div>
    </div>
  );
};
